import type { PowerCurvePoint } from "../../lib/types";
import { MicroLabel } from "../ui/Text";

/**
 * Spec 7's power curve, drawn.
 *
 * ## Why hand-rolled SVG
 *
 * Twenty points. A charting library would be the largest dependency in the panel
 * (the bundle budget for an app page is 300 kB) to draw a polyline, and it would
 * arrive with its own type scale, its own grey, and its own tooltip — three things
 * this interface already has opinions about. Everything below is `path` and
 * `line`, in the tokens the rest of the panel uses.
 *
 * ## A live curve is drawn differently on purpose
 *
 * A consolidated curve is final; a live one moves on the next MeterValues. Solid
 * versus dashed, the same distinction `QuotaBar` draws between a committed figure
 * and an estimate — and, like there, it survives greyscale and colour-blindness
 * where a second hue would not.
 *
 * ## No axis furniture
 *
 * Peak and average are printed as figures beside the chart, where they can be read
 * exactly, so gridlines would only add ink. The one horizontal line drawn is the
 * average, because "was this session steady or spiky" is the question the shape
 * exists to answer and the mean is what the shape is spiky *around*.
 */

/** The viewBox. Aspect ratio only — the SVG scales to its container. */
const WIDTH = 720;
const HEIGHT = 180;
const PAD_Y = 12;

export function PowerCurve({
  curve,
  avgPowerW,
  isLive,
}: {
  readonly curve: readonly PowerCurvePoint[];
  readonly avgPowerW: number | null;
  readonly isLive: boolean;
}) {
  /**
   * One point is not a shape. Drawing a single-sample session as a flat line
   * across the full width would imply a duration's worth of steady power from one
   * reading, so it is drawn as the mark it is.
   */
  const peak = Math.max(...curve.map((point) => point.w));
  // Headroom above the peak, so the highest point is not welded to the top edge.
  // A zero-power session would divide by zero, so the floor is 1 W.
  const scaleMax = Math.max(peak, 1) * 1.1;

  const x = (index: number) =>
    curve.length === 1 ? WIDTH / 2 : (index / (curve.length - 1)) * WIDTH;
  const y = (watts: number) => HEIGHT - PAD_Y - (watts / scaleMax) * (HEIGHT - PAD_Y * 2);

  const segments = curve.map((point, index) => `${x(index)},${y(point.w)}`);
  const line = `M${segments.join(" L")}`;
  // Closed back down to the baseline at both ends, so the fill is the area under
  // the curve rather than a filled polygon between its own points.
  const area = `M0,${HEIGHT} L${segments.join(" L")} L${WIDTH},${HEIGHT} Z`;

  const avgY = avgPowerW === null ? undefined : y(avgPowerW);

  return (
    <figure className="m-0 flex flex-col gap-2">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Power over the session, peaking at ${Math.round(peak)} watts`}
        className="h-40 w-full border border-rule bg-paper-sunken"
      >
        {curve.length > 1 ? (
          <>
            {/* The fill is what makes the shape legible at 160px tall; the stroke
                is what makes the individual readings legible. */}
            <path d={area} fill="var(--color-ink)" fillOpacity={0.08} />
            <path
              d={line}
              fill="none"
              stroke="var(--color-ink)"
              strokeWidth={2}
              strokeLinejoin="round"
              {...(isLive ? { strokeDasharray: "6 4" } : {})}
            />
          </>
        ) : null}

        {/* Every reading, marked. At twenty points these are the data; at one
            point this is the entire chart. */}
        {curve.map((point, index) => (
          <rect
            key={point.t}
            x={x(index) - 2}
            y={y(point.w) - 2}
            width={4}
            height={4}
            fill="var(--color-ink)"
          />
        ))}

        {avgY === undefined ? null : (
          <line
            x1={0}
            x2={WIDTH}
            y1={avgY}
            y2={avgY}
            stroke="var(--color-ink-faint)"
            strokeWidth={1}
            strokeDasharray="2 4"
          />
        )}
      </svg>

      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <MicroLabel>
          {isLive ? "Live · dashed" : "Consolidated"} · peak {Math.round(peak / 1000)} kW
        </MicroLabel>
        {avgPowerW === null ? null : (
          <MicroLabel>Dotted line: average {Math.round(avgPowerW / 1000)} kW</MicroLabel>
        )}
      </div>
    </figure>
  );
}
